import React, { useEffect, useState, useMemo } from "react";
import { getApi } from "../../../Services/apiService";
import {
  getMemberAgeLabel,
  getMemberDobTimestamp,
} from "../../../Utils/family-tree/dateAndTime";
import { formatMMDDYYYYToDayWeekdayDisplay } from "../../../Utils/dateUtils";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  isDashboardStartOrTopOfLineMember,
  isExplicitDashboardStartOrTopOfLineMember,
} from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardPeriodPickers, {
  DASHBOARD_PERIOD_ALL_MONTHS as ALL_MONTHS,
  DASHBOARD_PERIOD_MONTH_NAMES,
} from "./DashboardPeriodPickers";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import {
  applyDashboardMemberFilterToggle,
  getDashboardBaseSortForMemberFilter,
  filterDashboardMembersByMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";
import {
  sortDashboardMembersByAgeWithInLawsAlphabetical,
  applyDashboardInLawsLastOrder,
  sortDashboardMembersByDeathDate,
} from "../../../Utils/dashboardFamilyLineNumbers";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

const isInLawMember = (member) => {
  const isInLaw =
    member?.is_in_laws === true ||
    member?.is_in_laws === "true" ||
    member?.is_in_laws === 1;
  return Boolean(isInLaw && member?.head_first_name);
};

const shouldShowBirthdayAge = (member) => !isInLawMember(member);

function stripAgeFromDateDisplay(dateDisplay) {
  if (!dateDisplay || typeof dateDisplay !== "string") return dateDisplay || "";
  return dateDisplay.replace(/\s*-\s*\d+\s*Years?/i, "").trim();
}

/**
 * The age the member turns in `year` — the year the list is read against. The
 * payload's own `turning_age` is for the current year, so any other year moves
 * it on by the difference; a DOB alone gives it directly.
 */
function getBirthdayTurningAge(member, year) {
  const currentYear = new Date().getUTCFullYear();
  const yearOffset = Number.isFinite(year) ? year - currentYear : 0;
  const base = getCurrentYearTurningAge(member);
  return base == null ? null : base + yearOffset;
}

/**
 * The turning age as the row prints it. A baby turning 0 — born this year —
 * reads in months (see getMemberAgeLabel) rather than a bare 0.
 */
function getBirthdayTurningAgeLabel(member, year) {
  const turningAge = getBirthdayTurningAge(member, year);
  if (turningAge == null) return null;
  return turningAge === 0 ? getMemberAgeLabel(member, 0) : String(turningAge);
}

function getCurrentYearTurningAge(member) {
  if (member?.turning_age !== undefined && member?.turning_age !== null && member?.turning_age !== "") {
    const parsed = parseInt(member.turning_age, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  if (member?.age !== undefined && member?.age !== null && member?.age !== "") {
    const parsed = parseInt(member.age, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const dateStr = member?.date;
  if (dateStr && typeof dateStr === "string") {
    const yearsMatch = dateStr.match(/-\s*(\d+)\s*Years?/i);
    if (yearsMatch) return parseInt(yearsMatch[1], 10);
  }

  const dobMs = getMemberDobTimestamp(member);
  if (dobMs !== -1) {
    const dob = new Date(dobMs);
    const currentYear = new Date().getUTCFullYear();
    return currentYear - dob.getUTCFullYear();
  }

  return null;
}

function compareBirthdayCalendarDays(dayA, dayB) {
  if (dayA === -1 && dayB === -1) return 0;
  if (dayA === -1) return 1;
  if (dayB === -1) return -1;
  return dayA - dayB;
}

/** Same-day birthdays list the oldest member first (earliest DOB, else highest age). */
function compareBirthdayOldestFirst(a, b) {
  const dobA = getMemberDobTimestamp(a);
  const dobB = getMemberDobTimestamp(b);
  if (dobA !== -1 && dobB !== -1 && dobA !== dobB) return dobA - dobB;
  if (dobA !== -1 && dobB === -1) return -1;
  if (dobA === -1 && dobB !== -1) return 1;

  const ageA = getCurrentYearTurningAge(a);
  const ageB = getCurrentYearTurningAge(b);
  if (ageA != null && ageB != null && ageA !== ageB) return ageB - ageA;
  if (ageA != null && ageB == null) return -1;
  if (ageA == null && ageB != null) return 1;

  return 0;
}

function sortBirthdaysByCalendarDay(list, direction, getDay, getName) {
  return [...list].sort((a, b) => {
    const dayCompare = compareBirthdayCalendarDays(getDay(a), getDay(b));
    if (dayCompare !== 0) {
      return direction === "asc" ? dayCompare : -dayCompare;
    }

    const ageCompare = compareBirthdayOldestFirst(a, b);
    if (ageCompare !== 0) return ageCompare;

    const nameA = getName(a).toLowerCase();
    const nameB = getName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

/** Birthdays-this-month page: show "2" not "02" for single-digit days. */
function normalizeBirthdayDayDisplay(dateDisplay) {
  if (!dateDisplay || typeof dateDisplay !== "string") return dateDisplay || "";

  const weekdayFirst = dateDisplay.match(/^\(([^)]+)\)\s*0?(\d{1,2})(.*)$/);
  if (weekdayFirst) {
    const [, weekday, dayNum, trailing] = weekdayFirst;
    return `(${weekday}) ${parseInt(dayNum, 10)}${trailing}`;
  }

  const dayFirst = dateDisplay.match(/^0?(\d{1,2})\s*\(([^)]+)\)(.*)$/);
  if (dayFirst) {
    const [, dayNum, weekday, trailing] = dayFirst;
    return `${parseInt(dayNum, 10)} (${weekday})${trailing}`;
  }

  return dateDisplay;
}

const BIRTHDAY_MONTH_NAMES = DASHBOARD_PERIOD_MONTH_NAMES;

const BIRTHDAY_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function getWeekdayForCalendarDate(year, monthIndex, day) {
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (isNaN(date.getTime())) return "";
  return BIRTHDAY_WEEKDAY_NAMES[date.getUTCDay()] || "";
}

function getUtcCalendarPartsFromDobMs(dobMs) {
  const date = new Date(Number(dobMs));
  if (isNaN(date.getTime())) return null;

  const utcMidnight = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );
  const normalized = new Date(utcMidnight);
  return {
    month: normalized.getUTCMonth(),
    day: normalized.getUTCDate(),
  };
}

/** Month/day from DOB; weekday uses the year the list is read against. */
function getBirthdayMonthDayFromMember(member) {
  const dobMs = getMemberDobTimestamp(member);
  if (dobMs !== -1) {
    const fromDob = getUtcCalendarPartsFromDobMs(dobMs);
    if (fromDob) return fromDob;
  }

  const dateStr = member?.date;
  if (!dateStr || typeof dateStr !== "string") return null;

  const trimmed = dateStr.trim();

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/\d{4}/);
  if (slashMatch) {
    return {
      month: parseInt(slashMatch[1], 10) - 1,
      day: parseInt(slashMatch[2], 10),
    };
  }

  for (let monthIndex = 0; monthIndex < BIRTHDAY_MONTH_NAMES.length; monthIndex++) {
    const monthName = BIRTHDAY_MONTH_NAMES[monthIndex];
    if (!trimmed.startsWith(`${monthName} `)) continue;

    const rest = trimmed.slice(monthName.length + 1);
    const dayMatch = rest.match(/^(\d{1,2})/);
    if (!dayMatch) continue;

    return {
      month: monthIndex,
      day: parseInt(dayMatch[1], 10),
    };
  }

  return null;
}

function formatBirthdayDayWeekdayFromMember(member, year) {
  const parts = getBirthdayMonthDayFromMember(member);
  if (!parts) return "";

  const weekday = getWeekdayForCalendarDate(year, parts.month, parts.day);
  const dayNum = parts.day;
  return weekday ? `(${weekday}) ${dayNum}` : String(dayNum);
}

/** Compact layout shows two-letter weekdays: Sunday -> SU, Monday -> MO, ... */
const WEEKDAY_ABBREVIATIONS = {
  sunday: "SU",
  monday: "MO",
  tuesday: "TU",
  wednesday: "WE",
  thursday: "TH",
  friday: "FR",
  saturday: "SA",
};

const abbreviateWeekday = (dayOfWeek) => {
  const weekday = String(dayOfWeek || "").trim();
  return (
    WEEKDAY_ABBREVIATIONS[weekday.toLowerCase()] || weekday.slice(0, 2).toUpperCase()
  );
};

function BirthdaysThisWeek() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  // The full roster: the colour legend is built from it, and so is every month
  // other than the current one — see periodMonths.
  const [rosterMembers, setRosterMembers] = useState([]);
  // The payload's own birthdays_this_month rows, which is what the dashboard
  // card counts, so the current month here always matches that card.
  const [currentMonthBirthdays, setCurrentMonthBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  // 1–12, or ALL_MONTHS for the whole year.
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [nameColumnWidth, setNameColumnWidth] = useState("auto");
  const [dateColumnWidth, setDateColumnWidth] = useState("auto");
  const [ageColumnWidth, setAgeColumnWidth] = useState("auto");
  const [headOfLineColumnWidth, setHeadOfLineColumnWidth] = useState("auto");
  const [weekdayColumnWidth, setWeekdayColumnWidth] = useState("auto");
  const [sortBy, setSortBy] = useState("date-asc"); // date-asc: day 1→30, date-desc: day 30→1
  const [memberFilter, setMemberFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLargeView, setIsLargeView] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // A member tab on the right of the bar's divider is selected on its own: it
  // clears every sort on the left, Date included, and the list is ordered by the
  // tab's own order below without a sort button lit for it.
  //
  // The sort side effect runs here in the click handler, not inside a
  // setMemberFilter updater: updaters run in the render phase, where React does not
  // reliably apply a setState for another piece of state — the list sorted but the
  // sort bar never lit the matching button up.
  const toggleMemberFilter = (filter) => {
    setMemberFilter(
      applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy)
    );
  };

  // What the list is actually ordered by. With no sort lit, that is the order
  // the active member tab is read in, falling back to this page's own order:
  // birthdays read by date.
  //
  // The two in-law tabs are the exception. Their shared base order reads them as
  // a name list (living) and a year-of-death list (deceased), which is right on
  // the pages that are plain member lists — but this page is keyed to a birthday,
  // where the year is not what the reader is scanning for. Both stay on the page's
  // own day-of-month order, so 2 comes before 14 whatever year each was born.
  const effectiveSort =
    sortBy === "none"
      ? memberFilter === "in-laws" || memberFilter === "in-laws-deceased"
        ? "date-asc"
        : getDashboardBaseSortForMemberFilter(memberFilter, "date-asc")
      : sortBy;

  // The months the pickers ask for, each with its rows. The current month is
  // the payload's own list; the endpoint names no other month, so every other
  // one is read off the roster by birth month. The API list carries the same
  // people the roster does, only with the current year's turning age and
  // weekday already spelled out — so the rows print the same either way.
  const currentMonthIndex = now.getMonth();
  const periodMonths = useMemo(() => {
    const monthIndexes =
      month === ALL_MONTHS
        ? BIRTHDAY_MONTH_NAMES.map((_, index) => index)
        : [month - 1];

    return monthIndexes.map((monthIndex) => ({
      monthIndex,
      monthName: BIRTHDAY_MONTH_NAMES[monthIndex],
      rows:
        monthIndex === currentMonthIndex
          ? currentMonthBirthdays
          : rosterMembers.filter(
              (member) => getBirthdayMonthDayFromMember(member)?.month === monthIndex
            ),
    }));
  }, [month, currentMonthIndex, currentMonthBirthdays, rosterMembers]);

  // Fetch birthdays from API
  useEffect(() => {
    const fetchBirthdays = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setCurrentMonthBirthdays([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          setRosterMembers(extractAllMembersFromStatisticsResponse(response.data));
          // Extract birthdays from the response
          // The API returns: data.birthdays_this_month.details (array)
          const birthdaysData = 
            response?.data?.birthdays_this_month?.details || 
            response?.data?.birthdays_this_month ||
            [];
          
          // Ensure it's always an array
          if (Array.isArray(birthdaysData)) {
            setCurrentMonthBirthdays(birthdaysData);
          } else {
            console.warn("Birthdays data is not an array:", birthdaysData);
            setCurrentMonthBirthdays([]);
          }
        } else {
          setCurrentMonthBirthdays([]);
        }
      } catch (error) {
        console.error("Error fetching birthdays:", error);
        setCurrentMonthBirthdays([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBirthdays();
  }, []);

  const birthdayNameOptions = {
    unnamedLabel: "",
    strictHeadForInLaw: true,
    appendDescendantSpouse: false,
  };

  const birthdayInLawSortOptions = useMemo(
    () => ({
      isListedInLaw: (member) =>
        isInLawMember(member) && !isDashboardStartOrTopOfLineMember(member),
      separateStartTop: true,
      nameOptions: birthdayNameOptions,
    }),
    []
  );

  const formatPersonName = (member) =>
    formatDashboardMemberDisplayName(member, birthdayNameOptions);

  // The member tab narrows each month first, then the search box narrows it
  // again on the name as printed, so the count reads the rows on screen.
  const filteredMonths = useMemo(
    () =>
      periodMonths.map((group) => ({
        ...group,
        rows: filterDashboardMembersBySearch(
          filterDashboardMembersByMemberFilter(group.rows, memberFilter),
          searchQuery,
          formatPersonName
        ),
      })),
    [periodMonths, memberFilter, searchQuery]
  );

  const filteredBirthdays = useMemo(
    () => filteredMonths.flatMap((group) => group.rows),
    [filteredMonths]
  );

  const isSearching = searchQuery.trim().length > 0;

  // Helper function to format date by removing month name (since all are in the same month)
  const formatDateWithoutMonth = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      return "";
    }

    const fromSlash = formatMMDDYYYYToDayWeekdayDisplay(dateString);
    if (fromSlash) return normalizeBirthdayDayDisplay(fromSlash);
    
    // Remove month name from the beginning (e.g., "January 7 (Wednesday) - 29 Years" -> "7 (Wednesday) - 29 Years")
    // Pattern: Month name followed by space, then day number
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    let formatted = dateString.trim();
    
    // Remove month name if present
    for (const month of monthNames) {
      if (formatted.startsWith(month + ' ')) {
        formatted = formatted.substring(month.length + 1).trim();
        break;
      }
    }
    
    // Reorder to "(DayOfWeek) DayNumber" when a day of week is present, keep any trailing text (e.g., years)
    const dayWithWeekMatch = formatted.match(/^(\d{1,2})\s*\(([^)]+)\)(.*)$/);
    if (dayWithWeekMatch) {
      const [, dayNum, dayOfWeek, trailing] = dayWithWeekMatch;
      return normalizeBirthdayDayDisplay(
        `(${dayOfWeek}) ${parseInt(dayNum, 10)}${trailing}`
      );
    }
    
    return normalizeBirthdayDayDisplay(formatted);
  };

  // Split formatted date into parts to align numbers vertically
  const splitDateParts = (dateDisplay) => {
    if (!dateDisplay || typeof dateDisplay !== 'string') {
      return { dayOfWeek: "", dayNum: "", trailingText: "" };
    }
    const match = dateDisplay.match(/^\(([^)]+)\)\s*(\d{1,2})(.*)$/);
    if (match) {
      return {
        dayOfWeek: match[1],
        dayNum: match[2],
        trailingText: (match[3] || "").trimStart(),
      };
    }
    return { dayOfWeek: "", dayNum: "", trailingText: "" };
  };

  // Helper function to extract day number from date string
  const extractDayNumber = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      // Fallback: try to parse from DOB
      return -1;
    }

    let formatted = dateString.trim();

    // MM/DD/YYYY (optional trailing weekday in parentheses)
    const slashMatch = formatted.match(/^(\d{1,2})\/(\d{1,2})\/\d{4}/);
    if (slashMatch) {
      return parseInt(slashMatch[2], 10);
    }
    
    // Remove month name first
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    for (const month of monthNames) {
      if (formatted.startsWith(month + ' ')) {
        formatted = formatted.substring(month.length + 1).trim();
        break;
      }
    }
    
    // Remove day of week in parentheses if present
    formatted = formatted.replace(/\s*\([^)]+\)/g, '').trim();
    
    // Extract the day number (first number in the string)
    const dayMatch = formatted.match(/^\d+/);
    if (dayMatch) {
      return parseInt(dayMatch[0], 10);
    }
    
    // Fallback: try to parse from DOB if available
    return -1;
  };

  /** Day from DOB; weekday for that month/day in the year picked. */
  const birthdayDateString = (member) =>
    formatBirthdayDayWeekdayFromMember(member, year);

  const birthdayDayForSort = (member) => {
    if (member?.dob) {
      const t = parseInt(member.dob, 10);
      if (!Number.isNaN(t)) return new Date(t).getUTCDate();
    }
    const fromStr = extractDayNumber(member?.date || "");
    if (fromStr !== -1) return fromStr;
    return -1;
  };

  // Helper function to render member row with 4 columns
  const renderMemberRow = (member) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(
      member,
      birthdayNameOptions
    );
    const isDeceased = member?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    const showInLawAsterisk =
      isInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member);
    const showAge = shouldShowBirthdayAge(member);
    const turningAge = showAge ? getBirthdayTurningAgeLabel(member, year) : null;
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const hexCode = member?.hex_code || "#000000";
    const colorName = member?.color_name || "";

    let dateDisplay = birthdayDateString(member);
    
    // Remove month name from date display
    dateDisplay = stripAgeFromDateDisplay(formatDateWithoutMonth(dateDisplay));

    // Calculate sizes based on large view mode
    const iconSize = isLargeView ? '18px' : '12px';

    return (
      <div className={`dashboard-detail-row ${isLargeView ? 'large-view' : ''}`}>
        {/* Column 1: Date */}
        <div
          className={`dashboard-detail-date-col ${isLargeView ? 'large-view' : ''}`}
          style={{
            width: dateColumnWidth,
            marginRight: isLargeView ? '2ch' : '3ch',
            marginLeft: isCompactLayout ? '0' : '2ch',
            gridTemplateColumns: `2ch ${weekdayColumnWidth} auto`,
            alignItems: 'center',
          }}
        >
          {(() => {
            const { dayOfWeek, dayNum, trailingText } = splitDateParts(dateDisplay);
            if (!dayOfWeek) return dateDisplay;
            return (
              <>
                <span className="dashboard-detail-date-day">{dayNum}</span>
                <span className="dashboard-detail-date-weekday" style={{ width: weekdayColumnWidth }}>
                  ({isCompactLayout ? abbreviateWeekday(dayOfWeek) : dayOfWeek})
                </span>
                {trailingText ? (
                  <span className="dashboard-detail-date-trailing">{trailingText}</span>
                ) : null}
              </>
            );
          })()}
        </div>

        {/* Column 2: Age (non in-laws) */}
        <div
          className={`dashboard-detail-age-col ${isLargeView ? "large-view" : ""}`}
          style={{
            width: collapseAgeColumn ? "0" : ageColumnWidth,
            marginRight: collapseAgeColumn
              ? "0"
              : isLargeView
                ? "5ch"
                : "3ch",
          }}
        >
          {turningAge != null ? (
            <span className="dashboard-detail-age-num">{turningAge}</span>
          ) : null}
        </div>
        
        {/* Column 3: Person Name */}
        <div className={`dashboard-detail-name-col ${isLargeView ? 'large-view' : ''}`} style={{ width: nameColumnWidth, marginLeft: !isCompactLayout && !isLargeView ? '0' : undefined, marginRight: isLargeView ? '5ch' : '3ch' }}>
        <DashboardNameMarkerGroup>
        <DashboardPhotoName
          {...getDashboardMemberNamePhotoUrls(member)}
          onViewPhoto={openPhoto}
        >
          <DashboardMemberNameLabel
            baseName={baseName}
            descendantSpouseName={descendantSpouseName}
            afterBaseName={
              showInLawAsterisk && descendantSpouseName ? (
                <DashboardInLawAsterisk className="birthdays-this-week-page in-law-asterisk" isLargeView={isLargeView} />
              ) : null
            }
          />
        </DashboardPhotoName>
        {showInLawAsterisk && !descendantSpouseName && (
          <DashboardInLawAsterisk className="birthdays-this-week-page in-law-asterisk" isLargeView={isLargeView} />
        )}
        {isDeceased && (
            <span className={`dashboard-detail-angel-icon ${isLargeView ? 'large-view' : ''}`}>
            <img 
              src={window.getPublicUrl("/assets/images/angel.png")} 
              alt="deceased"
              style={{ width: iconSize, height: iconSize }}
            />
          </span>
        )}
        {multipleBirthSup && (
          <sup className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}>{multipleBirthSup}</sup>
        )}
        </DashboardNameMarkerGroup>
        </div>
        
        {/* Column 4: Head of Line Name and Color — compact prints the line colour in
            its own colour, and the footer legend ties each colour to its head */}
        {isCompactLayout ? (
          <div className="dashboard-detail-head-col dashboard-detail-line-col--compact">
            <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
          </div>
        ) : (
          <div className={`dashboard-detail-head-col ${isLargeView ? 'large-view' : ''}`} style={{ width: headOfLineColumnWidth, marginLeft: isLargeView ? undefined : '0' }}>
            <div className={isLargeView ? 'large-view-text' : ''}>
              <DashboardPhotoHeadLine
                name={headOfLineName || "Start/Top of Line"}
                hexCode={hexCode}
                colorName={colorName ? `${colorName} Line` : ""}
                imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
                onViewPhoto={openPhoto}
                member={member}
                headsMap={headsMap}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Sort birthdays based on sortBy (after in-law / descendants filter). The
  // month grouping is the period's own shape and stands under every sort; only
  // the rows inside a month re-order. Date descending also reads the months
  // last to first, so a whole-year list runs December back to January.
  const sortedMonths = useMemo(() => {
    const sortBirthdayRows = (filteredBirthdays) => {
      if (effectiveSort === "none") {
        return applyDashboardInLawsLastOrder(
          filteredBirthdays,
          memberFilter,
          birthdayInLawSortOptions
        );
      }

      if (effectiveSort === "death-asc") {
        return sortDashboardMembersByDeathDate(filteredBirthdays, {
          appendDescendantSpouse: false,
        });
      }

      // A–Z and Z–A read the whole list the tab is showing — in-laws sit in the
      // run with everyone else instead of being parked at the end of it. Only the
      // two age sorts park them, and that is because they are not ranked by age.
      if (effectiveSort === "alpha-asc") {
        return [...filteredBirthdays].sort((a, b) => {
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }

      if (effectiveSort === "alpha-desc") {
        return [...filteredBirthdays].sort((a, b) => {
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          return nameB.localeCompare(nameA);
        });
      }

      // In-laws are not counted in anything involving age, so both age sorts rank
      // the descendants only — in-laws on screen stay A–Z among themselves,
      // including when the In-Laws tab is the list being shown.
      if (effectiveSort === "youngest") {
        return sortDashboardMembersByAgeWithInLawsAlphabetical(
          filteredBirthdays,
          (list) =>
            [...list].sort((a, b) => {
              const dobA = getMemberDobTimestamp(a);
              const dobB = getMemberDobTimestamp(b);

              if (dobA === -1 && dobB === -1) return 0;
              if (dobA === -1) return 1;
              if (dobB === -1) return -1;
              return dobB - dobA;
            }),
          birthdayInLawSortOptions
        );
      }

      if (effectiveSort === "oldest") {
        return sortDashboardMembersByAgeWithInLawsAlphabetical(
          filteredBirthdays,
          (list) =>
            [...list].sort((a, b) => {
              const dobA = getMemberDobTimestamp(a);
              const dobB = getMemberDobTimestamp(b);

              if (dobA === -1 && dobB === -1) return 0;
              if (dobA === -1) return 1;
              if (dobB === -1) return -1;
              return dobA - dobB;
            }),
          birthdayInLawSortOptions
        );
      }

      if (effectiveSort === "color") {
        // Same as Total Family Members: in-laws sit inside their own line/colour
        // group with everyone else instead of being parked at the end of the list.
        return [...filteredBirthdays].sort((a, b) => {
          const colorA = (a?.color_name || "").toLowerCase();
          const colorB = (b?.color_name || "").toLowerCase();
          if (colorA !== colorB) {
            return colorA.localeCompare(colorB);
          }
          // All Months reads the year month by month, so inside each colour the
          // rows run in birthday order, then A–Z for a shared day.
          if (month === ALL_MONTHS) {
            const dayCompare = compareBirthdayCalendarDays(
              birthdayDayForSort(a),
              birthdayDayForSort(b)
            );
            if (dayCompare !== 0) return dayCompare;
            return formatPersonName(a)
              .toLowerCase()
              .localeCompare(formatPersonName(b).toLowerCase());
          }
          // Same line/colour: A–Z by name, then calendar day for namesakes.
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          const nameCompare = nameA.localeCompare(nameB);
          if (nameCompare !== 0) return nameCompare;

          const dayA = birthdayDayForSort(a);
          const dayB = birthdayDayForSort(b);
          return compareBirthdayCalendarDays(dayA, dayB);
        });
      }

      if (effectiveSort === "date-asc") {
        return sortBirthdaysByCalendarDay(
          filteredBirthdays,
          "asc",
          birthdayDayForSort,
          formatPersonName
        );
      }

      if (effectiveSort === "date-desc") {
        return sortBirthdaysByCalendarDay(
          filteredBirthdays,
          "desc",
          birthdayDayForSort,
          formatPersonName
        );
      }

      return applyDashboardInLawsLastOrder(
        filteredBirthdays,
        memberFilter,
        birthdayInLawSortOptions
      );
    };

    const months = filteredMonths
      .filter((group) => group.rows.length > 0)
      .map((group) => ({ ...group, rows: sortBirthdayRows(group.rows) }));

    return effectiveSort === "date-desc" ? months.reverse() : months;
  }, [filteredMonths, effectiveSort, memberFilter, birthdayInLawSortOptions, month]);

  const sortedBirthdays = useMemo(
    () => sortedMonths.flatMap((group) => group.rows),
    [sortedMonths]
  );

  // In-law lists never print a turning age, so on compact the age column is pure
  // empty space between the date and the name — collapse it there.
  const hasAnyBirthdayAge = useMemo(
    () =>
      sortedBirthdays.some(
        (member) =>
          shouldShowBirthdayAge(member) &&
          getBirthdayTurningAge(member, year) != null
      ),
    [sortedBirthdays, year]
  );
  const collapseAgeColumn = (isCompactLayout || !isLargeView) && !hasAnyBirthdayAge;

  const totalCount = filteredBirthdays.length;

  // Unfiltered on the current month keeps the full "Birthdays this Month"
  // heading. Any filter suffix makes that wrap onto a second line, so the
  // shorter "Birthdays" carries the title there — and it is the heading for
  // any other month or year too, where "this Month" would be wrong.
  const isCurrentMonthOfCurrentYear =
    month === now.getMonth() + 1 && year === now.getFullYear();
  const listTitle =
    memberFilter === "in-laws"
      ? "Birthdays — In-Laws (Living)"
      : memberFilter === "in-laws-deceased"
        ? "Birthdays — In-Laws (Deceased)"
        : memberFilter === "descendants-deceased"
          ? "Birthdays — Descendants (Deceased)"
          : memberFilter === "descendants-living"
            ? "Birthdays — Descendants (Living)"
            : isCurrentMonthOfCurrentYear
              ? "Birthdays this Month"
              : "Birthdays";

  // Built from the whole tree, not this page's rows, so the footer legend lists the
  // same family lines here as on every other dashboard list. Built from the rows it
  // only showed lines this page happened to contain.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(rosterMembers, headsMap),
    [rosterMembers, headsMap]
  );

  const showColorLegend = isCompactLayout && legendLines.length > 0;

  // Measure the actual width of name and head of line elements after render
  useEffect(() => {
    if (sortedBirthdays.length === 0 || loading) return;
    
    // Helper functions (local copies for useEffect)
    const getHeadOfLineNameLocal = (row) =>
      getDashboardFamilyLineHeadDisplayName(row, headsMap);

    const formatDateWithoutMonthLocal = (dateString) => {
      if (!dateString || typeof dateString !== 'string') {
        return "";
      }

      const fromSlash = formatMMDDYYYYToDayWeekdayDisplay(dateString);
      if (fromSlash) return normalizeBirthdayDayDisplay(fromSlash);
      
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      
      let formatted = dateString.trim();
      
      for (const month of monthNames) {
        if (formatted.startsWith(month + ' ')) {
          formatted = formatted.substring(month.length + 1).trim();
          break;
        }
      }
      
      const dayWithWeekMatch = formatted.match(/^(\d{1,2})\s*\(([^)]+)\)(.*)$/);
      if (dayWithWeekMatch) {
        const [, dayNum, dayOfWeek, trailing] = dayWithWeekMatch;
        return normalizeBirthdayDayDisplay(
          `(${dayOfWeek}) ${parseInt(dayNum, 10)}${trailing}`
        );
      }
      
      return normalizeBirthdayDayDisplay(formatted);
    };
    
    // Use appropriate font sizes based on view mode
    const fontSize = isLargeView ? '24px' : '16px';
    const iconSize = isLargeView ? 18 : 12;
    
    // Create temporary elements for measurement
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = 'inherit';
    document.body.appendChild(tempDiv);
    
    const chMeasure = document.createElement('div');
    chMeasure.style.position = 'absolute';
    chMeasure.style.visibility = 'hidden';
    chMeasure.style.fontSize = fontSize;
    chMeasure.style.fontFamily = 'inherit';
    chMeasure.textContent = '0';
    document.body.appendChild(chMeasure);
    const chWidth = chMeasure.getBoundingClientRect().width;

    // Create a measure for date column (page uses font-size: inherit = row size, not 14px base)
    const dateMeasure = document.createElement('div');
    dateMeasure.style.position = 'absolute';
    dateMeasure.style.visibility = 'hidden';
    dateMeasure.style.fontSize = fontSize;
    dateMeasure.style.fontFamily = 'inherit';
    document.body.appendChild(dateMeasure);
    
    let maxNameWidth = 0;
    let maxDateWidth = 0;
    let maxAgeWidth = 0;
    let maxHeadOfLineWidth = 0;
    let maxWeekdayWidth = 0;
    
    sortedBirthdays.forEach((member) => {
      const { baseName, descendantSpouseName } = getDashboardMemberNameParts(
        member,
        birthdayNameOptions
      );
      const isDeceased = member?.present_or_deceased === 2;
      const supText = getMultipleBirthSuperscriptText(member);

      // Mirrors DashboardMemberNameLabel — the spouse half renders bold, so
      // measuring the whole name at regular weight came up short of the row.
      tempDiv.innerHTML = "";
      const nameSpan = document.createElement("span");
      nameSpan.textContent = baseName;
      tempDiv.appendChild(nameSpan);

      if (descendantSpouseName) {
        const open = document.createElement("span");
        open.textContent = " (";
        tempDiv.appendChild(open);
        const spouse = document.createElement("strong");
        spouse.style.fontWeight = "700";
        spouse.textContent = descendantSpouseName;
        tempDiv.appendChild(spouse);
        const close = document.createElement("span");
        close.textContent = ")";
        tempDiv.appendChild(close);
      }

      if (
        isInLawMember(member) &&
        !isExplicitDashboardStartOrTopOfLineMember(member)
      ) {
        const ast = document.createElement("span");
        ast.textContent = "*";
        ast.style.fontSize = fontSize;
        // .dashboard-in-law-asterisk is bold in every layout
        ast.style.fontWeight = "700";
        tempDiv.appendChild(ast);
      }

      if (isDeceased) {
        const iconWrap = document.createElement("span");
        iconWrap.style.display = "inline-block";
        iconWrap.style.width = `${iconSize}px`;
        iconWrap.style.height = `${iconSize}px`;
        iconWrap.style.verticalAlign = "middle";
        tempDiv.appendChild(iconWrap);
      }

      if (supText) {
        const sup = document.createElement("sup");
        sup.className = "dashboard-multiple-birth-sup";
        sup.textContent = supText;
        sup.style.fontSize = "0.75em";
        tempDiv.appendChild(sup);
      }

      const nameWidth = tempDiv.getBoundingClientRect().width;
      if (nameWidth > maxNameWidth) {
        maxNameWidth = nameWidth;
      }
      
      // Measure date width (without month name or age suffix)
      let dateDisplay = birthdayDateString(member);
      dateDisplay = stripAgeFromDateDisplay(formatDateWithoutMonthLocal(dateDisplay));
      const { dayOfWeek } = splitDateParts(dateDisplay);
      if (dayOfWeek) {
        tempDiv.textContent = `(${
          isCompactLayout ? abbreviateWeekday(dayOfWeek) : dayOfWeek
        })`;
        const weekdayWidth = tempDiv.getBoundingClientRect().width;
        if (weekdayWidth > maxWeekdayWidth) {
          maxWeekdayWidth = weekdayWidth;
        }
      }
      // Measure what is actually rendered — compact layout abbreviates the weekday
      dateMeasure.textContent =
        isCompactLayout && dayOfWeek
          ? dateDisplay.replace(`(${dayOfWeek})`, `(${abbreviateWeekday(dayOfWeek)})`)
          : dateDisplay;
      const dateWidth = dateMeasure.getBoundingClientRect().width;
      if (dateWidth > maxDateWidth) {
        maxDateWidth = dateWidth;
      }

      if (shouldShowBirthdayAge(member)) {
        const turningAge = getBirthdayTurningAgeLabel(member, year);
        if (turningAge != null) {
          tempDiv.textContent = turningAge;
          const ageWidth = tempDiv.getBoundingClientRect().width;
          if (ageWidth > maxAgeWidth) {
            maxAgeWidth = ageWidth;
          }
        }
      }
      
      // Measure head of line width
      const headOfLineName = getHeadOfLineNameLocal(member) || "Start/Top of Line";
      const colorName = member?.color_name || "";
      const colorText = colorName ? ` (●  ${colorName} Line)` : "";
      const headOfLineText = `${headOfLineName}${colorText}`;
      tempDiv.textContent = headOfLineText;
      const headOfLineWidth = tempDiv.getBoundingClientRect().width;
      if (headOfLineWidth > maxHeadOfLineWidth) {
        maxHeadOfLineWidth = headOfLineWidth;
      }
    });
    
    document.body.removeChild(tempDiv);
    document.body.removeChild(chMeasure);
    document.body.removeChild(dateMeasure);
    
    const nameWidthInCh =
      Math.ceil(maxNameWidth / chWidth) + (isLargeView ? 2 : 1);
    // Add spacing after the longest date — tightened on compact so the name column fits
    const dateWidthInCh =
      Math.ceil(maxDateWidth / chWidth) + (isLargeView ? 4 : isCompactLayout ? 1 : 3);
    const ageWidthInCh = Math.max(Math.ceil(maxAgeWidth / chWidth) + 1, 2);
    const headOfLineWidthInCh = Math.ceil(maxHeadOfLineWidth / chWidth);
    // Ensure room for the longest weekday, reduced by 1ch. Compact layout shows
    // two-letter weekdays, so the floor drops with them.
    const weekdayWidthInCh = Math.max(
      Math.ceil(maxWeekdayWidth / chWidth) - 1,
      isCompactLayout ? 4 : 6
    );
    // Desktop and phone size every column to exactly its longest entry, so the
    // 3ch margin between columns is the entire gap after that entry. The
    // ch-rounded widths above still drive large view.
    const isDesktopLayout = !isLargeView;
    // The date column is a grid: 2ch day number + 0.5ch column gap + weekday,
    // and the weekday text overflows its track when the track was trimmed.
    const dateContentPx =
      2.5 * chWidth + Math.max(maxWeekdayWidth, weekdayWidthInCh * chWidth);

    setNameColumnWidth(
      isDesktopLayout ? `${Math.ceil(maxNameWidth)}px` : `${nameWidthInCh}ch`
    );
    setDateColumnWidth(
      isDesktopLayout ? `${Math.ceil(dateContentPx)}px` : `${dateWidthInCh}ch`
    );
    setAgeColumnWidth(
      isDesktopLayout ? `${Math.ceil(maxAgeWidth)}px` : `${ageWidthInCh}ch`
    );
    setHeadOfLineColumnWidth(`${headOfLineWidthInCh}ch`);
    setWeekdayColumnWidth(`${weekdayWidthInCh}ch`);
  }, [sortedBirthdays, loading, isLargeView, isCompactLayout, year]);

  return (
    <div className={`dashboard-container birthdays-this-week-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
          <DashboardBackButton />
            <h3 className="dashboard-detail-title">{listTitle}</h3>
            <div className="dashboard-detail-count">
              {totalCount}
            </div>
            {!loading && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                endAtControl='[data-sort="youngest"]'
                centreOverDivider={false}
              />
            )}
            {/* Up whenever the list has loaded, empty month included — the
                pickers on it are how the reader moves to another month. */}
            {!loading && (
              <DashboardDetailSortFilterBar
                sortBy={sortBy}
                onSortChange={setSortBy}
                memberFilter={memberFilter}
                onMemberFilterToggle={toggleMemberFilter}
                excludedMemberFilters={[
                  "start-top",
                  "descendants",
                  "in-relationship",
                ]}
                descendantsDeceasedLabel="Desc Deceased"
                leadingSortButtons={
                  <>
                  <DashboardPeriodPickers
                    month={month}
                    onMonthChange={setMonth}
                    year={year}
                    onYearChange={setYear}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (sortBy === "date-asc") {
                        setSortBy("date-desc");
                      } else {
                        setSortBy("date-asc");
                      }
                    }}
                    className={`dashboard-detail-sort-btn ${
                      sortBy === "date-asc" || sortBy === "date-desc"
                        ? "active"
                        : ""
                    }`}
                    title="Sort by Date"
                  >
                    Date
                    {sortBy === "date-asc" ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginLeft: "4px" }}
                      >
                        <path d="M18 15l-6-6-6 6" />
                      </svg>
                    ) : sortBy === "date-desc" ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginLeft: "4px" }}
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    ) : (
                      <span
                        style={{
                          marginLeft: "4px",
                          display: "inline-flex",
                          flexDirection: "column",
                          lineHeight: 1,
                          alignItems: "center",
                        }}
                      >
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ display: "block" }}
                        >
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ display: "block", marginTop: "-2px" }}
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    )}
                  </button>
                  </>
                }
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading birthdays...</p>
          </div>
        ) : totalCount === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} noun="birthdays" />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="birthdays-this-week-page dashboard-detail-content-wrapper">
            <div className="birthdays-list dashboard-detail-list">
              {sortedMonths.map((group) => (
                <React.Fragment key={group.monthIndex}>
                  {/* Month header row */}
                  <div className={`dashboard-detail-row dashboard-detail-header-row ${isLargeView ? 'large-view' : ''}`}>
                    <div className={`dashboard-detail-date-col ${isLargeView ? 'large-view' : ''}`} style={{ width: dateColumnWidth, marginRight: isLargeView ? '2ch' : '3ch', marginLeft: isCompactLayout ? '0' : undefined }}>
                      <span className="dashboard-detail-month-name dashboard-section-heading">
                        {group.monthName}
                        {/* With All Months picked each heading carries its own count; a single month already shows it at the top. */}
                        {month === ALL_MONTHS && (
                          <span className="dashboard-section-heading-count">{` (${group.rows.length})`}</span>
                        )}
                      </span>
                    </div>
                    <div className="dashboard-detail-name-col" style={{ width: nameColumnWidth, marginLeft: isCompactLayout ? undefined : '0', marginRight: '3ch' }}>
                      {/* Empty for alignment */}
                    </div>
                    {!isCompactLayout && (
                      <div className="dashboard-detail-head-col" style={{ width: headOfLineColumnWidth, marginLeft: '0' }}>
                        {/* Empty for alignment */}
                      </div>
                    )}
                  </div>
                  {group.rows.map((member, index) => (
                    <div key={member?.id || index}>
                      {renderMemberRow(member)}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="birthdays-this-week-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(sortedBirthdays)}
              />
              {showColorLegend && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default BirthdaysThisWeek;
